import { foldArabicDigits, normalizeAreaName } from "@a2z/text";
import type {
  DriverSettlementCardView,
  DriverSettlementSubjectRef,
  SettlementBagState,
} from "@/lib/api/dispatchTypes";

/**
 * THE DRIVER SETTLEMENTS CARD GRID (B6 · P25, plan D-41) — what the Delivery
 * tab's «تسويات المندوبين» shows, derived from the server's cards
 * (`GET restaurant/dispatch/settlements/open`). Pure: no figure is computed
 * here that the server did not send; this only decides order, membership of
 * the filters and the search, and the card's small facts (done / total, the
 * returned / voided chips, settled or not).
 */

export type SettlementFilter = "toSettle" | "returns" | "all";

/** A stable key for a subject (React keys, the open panel). */
export function subjectKey(subject: DriverSettlementSubjectRef): string {
  return [subject.kind, subject.driverSellerId ?? "", subject.aggregatorId ?? "", subject.riderKey ?? ""].join("|");
}

/**
 * Settled today with nothing open: the green «Settled HH:MM» strip, dimmed and
 * last (the design). A card with something still to settle, or a bag still on
 * the way, keeps its Settle button even if it was settled earlier today.
 */
export function isSettledCard(card: DriverSettlementCardView): boolean {
  // B1: a driver settled this morning who has since ACCEPTED a new bag is not
  // "settled": the card keeps its button so that bag's controls stay reachable.
  return (
    !card.needsSettlement &&
    card.counts.onTheWay === 0 &&
    card.counts.waitingPickup === 0 &&
    card.lastSettledToday !== null
  );
}

/**
 * "done / total": the bags delivered (or out with a typed rider) over every
 * bag of the run, the ones still on the way included — «2 / 3» for two
 * delivered and one returned, «1 / 3» while two are still out.
 */
export function cardProgress(card: DriverSettlementCardView): { done: number; total: number } {
  return { done: card.counts.done, total: card.counts.onRun + card.counts.onTheWay };
}

export type CardFlag = { orderNumber: number; kind: "RETURNED" | "VOIDED" | "LEFT_OWED" };

/**
 * The flag chips: «#29 Voided», «#30 Returned», and a bag left owed at an
 * earlier settlement («#64 Left owed», D-42 ex. 10), in order-number order.
 * Empty → the card says «No returns or voids».
 */
export function cardFlags(card: DriverSettlementCardView): CardFlag[] {
  const flags: CardFlag[] = [];
  for (const bag of card.bags) {
    if (!bag.onRun) continue;
    const kind: CardFlag["kind"] | null =
      bag.state === "RETURNED"
        ? "RETURNED"
        : bag.state === "VOIDED"
          ? "VOIDED"
          : bag.leftOwedBefore
            ? "LEFT_OWED"
            : null;
    if (kind) flags.push({ orderNumber: bag.orderNumber, kind });
  }
  return flags.sort((a, b) => a.orderNumber - b.orderNumber);
}

export function matchesFilter(card: DriverSettlementCardView, filter: SettlementFilter): boolean {
  switch (filter) {
    case "toSettle":
      return !isSettledCard(card);
    case "returns":
      return card.hasReturns;
    case "all":
      return true;
  }
}

/** Search folding: Arabic letter variants and diacritics (`@a2z/text`), Arabic-Indic digits. */
export function foldSearch(text: string): string {
  return normalizeAreaName(foldArabicDigits(text));
}

/**
 * Name, code (a driver's phone last 4), platform name, and the till's own
 * label for a card (a rider with no typed name is «من غير اسم»).
 */
export function matchesSearch(card: DriverSettlementCardView, query: string, displayName: string): boolean {
  const needle = foldSearch(query);
  if (!needle) return true;
  return [displayName, card.name, card.code ?? "", card.aggregatorName ?? ""].some((field) =>
    foldSearch(field).includes(needle),
  );
}

/**
 * The design's order: open cards by the cash to collect, highest first (a
 * card with only bags on the way has 0 and so follows them), ties by name;
 * the settled ones last, the latest settled first.
 */
export function sortSettlementCards(
  cards: readonly DriverSettlementCardView[],
  nameOf: (card: DriverSettlementCardView) => string,
  locale: string,
): DriverSettlementCardView[] {
  return [...cards].sort((a, b) => {
    const settled = Number(isSettledCard(a)) - Number(isSettledCard(b));
    if (settled !== 0) return settled;
    if (isSettledCard(a)) {
      return (b.lastSettledToday?.confirmedAt ?? "").localeCompare(a.lastSettledToday?.confirmedAt ?? "");
    }
    const cash = b.sums.cashExpected - a.sums.cashExpected;
    if (cash !== 0) return cash;
    return nameOf(a).localeCompare(nameOf(b), locale);
  });
}

/** The cards a filter and a search leave, in the design's order, with the total for «n / m». */
export function visibleSettlementCards(
  cards: readonly DriverSettlementCardView[],
  filter: SettlementFilter,
  query: string,
  nameOf: (card: DriverSettlementCardView) => string,
  locale: string,
): { shown: DriverSettlementCardView[]; total: number } {
  const shown = sortSettlementCards(cards, nameOf, locale).filter(
    (card) => matchesFilter(card, filter) && matchesSearch(card, query, nameOf(card)),
  );
  return { shown, total: cards.length };
}

/**
 * Every invoice listed on a card that OPENS its panel (any card but a settled
 * one), so the tab can tell which held bags have nowhere else to show their
 * controls (B1): a bag only a settled card lists stays in «With a driver».
 */
export function invoicesOnCards(cards: readonly DriverSettlementCardView[]): Set<string> {
  return new Set(
    cards.filter((card) => !isSettledCard(card)).flatMap((card) => card.bags.map((bag) => bag.invoiceId)),
  );
}

/** The bag states that still sit with the driver — their dispatch actions are offered. */
export const LIVE_BAG_STATES: ReadonlySet<SettlementBagState> = new Set(["ON_THE_WAY", "WAITING_PICKUP"]);
